import { Loader2 } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import PublicContactSuccessCard from './PublicContactSuccessCard';
import {
  INQUIRY_TYPES,
  getContactSubmitErrorMessage,
  postContactSubmission,
  validateContactForm,
  type ContactFieldErrors,
  type ContactFormValues,
  type InquiryType,
} from './contactFormShared';
import useContactTurnstile from './useContactTurnstile';

type PublicContactFormProps = {
  defaultInquiryType?: InquiryType;
};

const INITIAL_FORM: ContactFormValues = {
  name: '',
  email: '',
  inquiryType: 'Product Support',
  subject: '',
  message: '',
  companyWebsite: '',
};

function buildInitialForm(defaultInquiryType: InquiryType): ContactFormValues {
  return {
    ...INITIAL_FORM,
    inquiryType: defaultInquiryType,
  };
}

export default function PublicContactForm({ defaultInquiryType = 'Product Support' }: PublicContactFormProps) {
  const initialForm = buildInitialForm(defaultInquiryType);
  const [form, setForm] = useState<ContactFormValues>(initialForm);
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [confirmationInquiryType, setConfirmationInquiryType] = useState<InquiryType | null>(null);
  const [confirmationTeamLabel, setConfirmationTeamLabel] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [deliveryNotice, setDeliveryNotice] = useState('');
  const isSubmittingRef = useRef(false);
  const {
    resetTurnstileWidget,
    turnstileError,
    turnstileHostRef,
    turnstileSiteKey,
    turnstileStatus,
    turnstileToken,
  } = useContactTurnstile();

  function updateField<K extends keyof ContactFormValues>(field: K, value: ContactFormValues[K]) {
    setForm((previous) => ({ ...previous, [field]: value }));
    setFieldErrors((previous) => ({ ...previous, [field]: '' }));
    setSubmitError('');
  }

  function resetForm() {
    setForm(initialForm);
    setFieldErrors({});
    setSubmitError('');
    setStatus('idle');
    setConfirmationInquiryType(null);
    setConfirmationTeamLabel('');
    setConfirmationEmail('');
    setReferenceId('');
    setDeliveryNotice('');
    resetTurnstileWidget();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'submitting' || isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const nextErrors = validateContactForm(form);
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSubmitError('');
      isSubmittingRef.current = false;
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setSubmitError('Please complete the security check before sending.');
      isSubmittingRef.current = false;
      return;
    }

    setStatus('submitting');
    setSubmitError('');

    try {
      const { response, body } = await postContactSubmission({
        ...form,
        turnstileToken: turnstileSiteKey ? turnstileToken : '',
      });

      if (!response.ok || !body?.ok) {
        resetTurnstileWidget();

        if (body?.fieldErrors) {
          setFieldErrors(body.fieldErrors);
        }

        setSubmitError(getContactSubmitErrorMessage(body?.error));
        setStatus('idle');
        return;
      }

      const resolvedInquiryType = body.inquiryType || form.inquiryType;
      setConfirmationInquiryType(resolvedInquiryType);
      setConfirmationTeamLabel(body.destinationTeamLabel || body.routedToLabel || 'Support');
      setConfirmationEmail(body.submitterEmail || form.email.trim());
      setReferenceId(body.referenceId || '');
      setDeliveryNotice(
        body.deliveryWarning === 'confirmation_email_failed'
          ? 'Your message was sent, but the confirmation email could not be delivered. Our team still received your request.'
          : ''
      );
      setStatus('success');
      resetTurnstileWidget();
    } catch {
      resetTurnstileWidget();
      setSubmitError('We could not send your message right now. Please try again in a moment.');
      setStatus('idle');
    } finally {
      isSubmittingRef.current = false;
    }
  }

  if (status === 'success' && confirmationInquiryType) {
    return (
      <PublicContactSuccessCard
        inquiryType={confirmationInquiryType}
        confirmationTeamLabel={confirmationTeamLabel}
        confirmationEmail={confirmationEmail}
        referenceId={referenceId}
        deliveryNotice={deliveryNotice}
        onReset={resetForm}
      />
    );
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit} noValidate aria-busy={status === 'submitting'}>
      {submitError ? (
        <div className="rounded-[20px] border border-pink-200 bg-pink-50 px-4 py-3 text-sm font-normal leading-6 text-pink-800">
          {submitError}
        </div>
      ) : null}

      <div className="hidden" aria-hidden="true">
        <label htmlFor="contact-company-website">Company website</label>
        <input
          id="contact-company-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.companyWebsite}
          onChange={(event) => updateField('companyWebsite', event.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500" htmlFor="contact-name">
            Name
          </label>
          <input
            id="contact-name"
            type="text"
            value={form.name}
            onChange={(event) => updateField('name', event.target.value)}
            autoComplete="name"
            className={`w-full rounded-xl border bg-white px-4 py-3 text-sm font-normal text-zinc-800 shadow-sm transition-all placeholder:text-zinc-400 focus:outline-none focus:ring-1 ${
              fieldErrors.name ? 'border-pink-300 focus:border-pink-400 focus:ring-pink-300' : 'border-zinc-200 focus:border-pink-400 focus:ring-pink-400'
            }`}
            placeholder="Full Name"
          />
          {fieldErrors.name ? <p className="text-xs font-normal text-pink-500">{fieldErrors.name}</p> : null}
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500" htmlFor="contact-email">
            Email
          </label>
          <input
            id="contact-email"
            type="email"
            value={form.email}
            onChange={(event) => updateField('email', event.target.value)}
            autoComplete="email"
            className={`w-full rounded-xl border bg-white px-4 py-3 text-sm font-normal text-zinc-800 shadow-sm transition-all placeholder:text-zinc-400 focus:outline-none focus:ring-1 ${
              fieldErrors.email ? 'border-pink-300 focus:border-pink-400 focus:ring-pink-300' : 'border-zinc-200 focus:border-pink-400 focus:ring-pink-400'
            }`}
            placeholder="email@address.com"
          />
          {fieldErrors.email ? <p className="text-xs font-normal text-pink-500">{fieldErrors.email}</p> : null}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500" htmlFor="contact-inquiry-type">
          Inquiry Type
        </label>
        <Select value={form.inquiryType} onValueChange={(value) => updateField('inquiryType', value as ContactFormValues['inquiryType'])}>
          <SelectTrigger
            id="contact-inquiry-type"
            className={`h-12 rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm font-normal text-zinc-100 shadow-sm transition-all focus:outline-none focus:ring-1 [&>svg]:mr-1.5 ${
              fieldErrors.inquiryType ? 'border-pink-500 focus:border-pink-500 focus:ring-pink-500' : 'focus:border-pink-500 focus:ring-pink-500'
            }`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-xl border border-zinc-200 bg-white text-zinc-800 shadow-xl">
            {INQUIRY_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {fieldErrors.inquiryType ? <p className="text-xs font-normal text-pink-500">{fieldErrors.inquiryType}</p> : null}
      </div>

      <div className="space-y-2">
        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500" htmlFor="contact-subject">
          Subject / Short Title
        </label>
        <input
          id="contact-subject"
          type="text"
          value={form.subject}
          onChange={(event) => updateField('subject', event.target.value)}
          className={`w-full rounded-xl border bg-white px-4 py-3 text-sm font-normal text-zinc-800 shadow-sm transition-all placeholder:text-zinc-400 focus:outline-none focus:ring-1 ${
            fieldErrors.subject ? 'border-pink-300 focus:border-pink-400 focus:ring-pink-300' : 'border-zinc-200 focus:border-pink-400 focus:ring-pink-400'
          }`}
          placeholder="Request title"
        />
        {fieldErrors.subject ? <p className="text-xs font-normal text-pink-500">{fieldErrors.subject}</p> : null}
      </div>

      <div className="space-y-2">
        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500" htmlFor="contact-message">
          Message
        </label>
        <textarea
          id="contact-message"
          rows={5}
          value={form.message}
          onChange={(event) => updateField('message', event.target.value)}
          className={`w-full resize-none rounded-xl border bg-white px-4 py-3 text-sm font-normal text-zinc-800 shadow-sm transition-all placeholder:text-zinc-400 focus:outline-none focus:ring-1 ${
            fieldErrors.message ? 'border-pink-300 focus:border-pink-400 focus:ring-pink-300' : 'border-zinc-200 focus:border-pink-400 focus:ring-pink-400'
          }`}
          placeholder="How can we help?"
        />
        {fieldErrors.message ? <p className="text-xs font-normal text-pink-500">{fieldErrors.message}</p> : null}
      </div>

      {turnstileSiteKey ? (
        <div className="space-y-2">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">Security check</p>
          <div className="rounded-2xl border border-pink-100 bg-white p-4 shadow-sm">
            <div ref={turnstileHostRef} />
            {turnstileStatus === 'loading' ? (
              <p className="mt-3 text-xs font-normal leading-6 text-zinc-500">Loading the security check...</p>
            ) : null}
            {turnstileStatus === 'error' ? (
              <p className="mt-3 text-xs font-normal leading-6 text-pink-500">
                {turnstileError || 'The security check could not load. Refresh the page and try again.'}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-2xl text-[12px] font-normal leading-6 text-zinc-500">
          Choose the topic that best fits your request. Trade-Proof routes each message directly to the right inbox.
        </div>
        <button
          type="submit"
          disabled={status === 'submitting' || (turnstileSiteKey ? !turnstileToken : false)}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-pink-600 px-10 py-3 text-[11px] font-black uppercase tracking-[0.3em] text-white shadow-xl transition-all active:scale-95 hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-70 md:w-auto"
        >
          {status === 'submitting' ? <Loader2 size={14} className="animate-spin" /> : null}
          {status === 'submitting' ? 'Sending' : 'SEND'}
        </button>
      </div>
    </form>
  );
}

export type { InquiryType, ContactFormValues, ContactFieldErrors };